import React, { useMemo, useState } from 'react';
import BasicModal from './modal';
import { updateVariables } from '../firebase';

import TextField from '@mui/material/TextField';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';

export default function VariablesModal({ open, onClose, data }) {
  const originalVariables = useMemo(() => (data && data.variables) || {}, [data]);
  const [rawValues, setRawValues] = useState(() => {
    const initial = {};
    Object.entries(originalVariables).forEach(([k, v]) => {
      initial[k] = Array.isArray(v) ? [...v] : (v ?? '');
    });
    return initial;
  });
  const [deletingItems, setDeletingItems] = useState(new Set());
  const [itemIds, setItemIds] = useState(() => {
    const ids = {};
    Object.entries(originalVariables).forEach(([k, v]) => {
      if (Array.isArray(v)) {
        ids[k] = v.map((_, i) => `${k}-${Date.now()}-${i}`);
      }
    });
    return ids;
  });

  const handleAddItem = (key) => {
    setRawValues(prev => ({ ...prev, [key]: [...(prev[key] || []), ''] }));
    setItemIds(prev => ({ ...prev, [key]: [...(prev[key] || []), `${key}-${Date.now()}`] }));
  };

  const handleDeleteItem = (key, index, itemKey) => {
    setDeletingItems(prev => new Set(prev).add(itemKey));
    setTimeout(() => {
      setRawValues(prev => {
        const newValues = [...prev[key]];
        newValues.splice(index, 1);
        return { ...prev, [key]: newValues };
      });
      setItemIds(prev => ({ ...prev, [key]: prev[key].filter((_, i) => i !== index) }));
      setDeletingItems(prev => { const next = new Set(prev); next.delete(itemKey); return next; });
    }, 300);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setRawValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const finalValues = {};
    Object.entries(originalVariables).forEach(([k, v]) => {
      if (Array.isArray(v)) {
        finalValues[k] = (rawValues[k] || []).filter(item => item.trim().length > 0);
      } else {
        finalValues[k] = rawValues[k];
      }
    });
    await updateVariables(data.id, finalValues);
    onClose();
  };

  const content = (
    Object.entries(originalVariables)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => {
        const isArray = Array.isArray(value);
        if (isArray) {
          const arrayValues = rawValues?.[key] || [];
          return (
            <Card key={key} sx={{ mt: 2, mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                  {key}
                </Typography>
                {arrayValues.map((item, index) => {
                  const itemKey = itemIds[key]?.[index];
                  const isDeleting = deletingItems.has(itemKey);
                  return (
                    <Box
                      key={itemKey}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        maxHeight: isDeleting ? 0 : '56px',
                        opacity: isDeleting ? 0 : 1,
                        overflow: 'hidden',
                        transition: 'all 0.2s ease-out'
                      }}
                    >
                      <TextField
                        margin="normal"
                        value={item}
                        fullWidth
                        size="small"
                        onChange={(e) => {
                          const newValues = [...arrayValues];
                          newValues[index] = e.target.value;
                          setRawValues(prev => ({ ...prev, [key]: newValues }));
                        }}
                      />
                      <IconButton size="small" onClick={() => handleDeleteItem(key, index, itemKey)} sx={{ ml: 1 }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  );
                })}
                <Button size="small" onClick={() => handleAddItem(key)} sx={{ mt: 1 }} variant="outlined">
                  + Add Item
                </Button>
              </CardContent>
            </Card>
          );
        }
        return (
          <TextField
            margin="normal"
            id={key}
            label={key}
            name={key}
            value={rawValues?.[key] ?? ''}
            fullWidth
            key={key}
            onChange={handleChange}
            InputProps={{ inputProps: { step: 0.01 } }}
          />
        );
      })
  );

  return (
    <BasicModal
      open={open}
      onClose={onClose}
      title="Edit Variables"
      onSubmit={handleSubmit}
      submitLabel="Save"
    >
      {content}
    </BasicModal>
  );
}
